import ConfirmDeleteDialog from "@/components/ConfirmDeleteDialog";
import { DownloadService } from "@/services/admin-services";
import { Download, DynamicDownload } from "@/services/admin-services/models";
import { LocaleService, VersionService } from "@/services/global-services";
import { Language, Locale } from "@/services/global-services/models";
import useDataStore from "@/stores/useDataStore";
import { getBytes } from "@/utils/getBytes";
import { PlusIcon } from "@heroicons/react/24/outline";
import {
    Box,
    Button,
    CircularProgress,
    Grid,
    Paper,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    Typography,
} from "@mui/material";
import { parseJSON } from "date-fns";
import { saveAs } from "file-saver";
import { useEffect, useState } from "react";
import CreateDialog from "./components/CreateDialog";
import { CreateCombinedDialog } from "./components/CreateDialog/CreateCombinedDialog";
import { EditDialog } from "./components/CreateDialog/EditDialog";

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function CustomTabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && children}
        </div>
    );
}

const DownloadManager = () => {
    const [loading, setLoading] = useState<boolean>(true);
    const [view, setView] = useState<number>(0);
    const [downloads, setDownloads] = useState<Download[]>([]);
    const [dynamicDownloads, setDynamicDownloads] = useState<DynamicDownload[]>([]);
    const [languages, setLanguages] = useState<Language[]>([]);

    const versions = useDataStore((state) => state.versions);
    const setVersions = useDataStore((state) => state.setVersions);
    const software = useDataStore((state) => state.software);
    const setSoftware = useDataStore((state) => state.setSoftware);
    const disciplines = useDataStore((state) => state.disciplines);
    const setDisciplines = useDataStore((state) => state.setDisciplines);
    const useCases = useDataStore((state) => state.useCases);
    const setUseCases = useDataStore((state) => state.setUseCases);

    const [addDownload, setAddDownload] = useState<boolean>(false);
    const [addCombinedDownload, setAddCombinedDownload] = useState<boolean>(false);
    const [editDownload, setEditDownload] = useState<Download | null>(null);
    const [deleteDownload, setDeleteDownload] = useState<string | null>(null);
    const [deleteDynamic, setDeleteDynamic] = useState<string | null>(null);

    const downloadService = new DownloadService("int");
    const localeService = new LocaleService("int", "en");

    useEffect(() => {
        Promise.all([
            downloadService.get().then(setDownloads),
            downloadService.getAllDynamic().then(setDynamicDownloads),
            LocaleService.get().then(parseLanguages),
        ]).then(() => setLoading(false));

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const downloadVersions = new Set(dynamicDownloads.map((x) => x.bidsVersion));
        VersionService.get().then((versions) => {
            setVersions(versions);

            versions.forEach((version) => {
                if (!downloadVersions.has(version.name)) return;

                localeService
                    .getSoftware(version.name)
                    .then((software) => setSoftware(version.id, software))
                    .catch(() => setSoftware(version.id, []));

                localeService
                    .getDisciplines(version.name)
                    .then((disciplines) => setDisciplines(version.id, disciplines))
                    .catch(() => setDisciplines(version.id, []));

                localeService
                    .getUseCases(version.name)
                    .then((useCases) => setUseCases(version.id, useCases))
                    .catch(() => setUseCases(version.id, []));
            });
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dynamicDownloads]);

    const parseLanguages = (regions: Locale[]) => {
        const languages: Language[] = [];
        for (let i = 0; i < regions.length; i++) {
            const region = regions[i];

            for (let j = 0; j < region.languages.length; j++) {
                const language = region.languages[j];
                if (languages.findIndex((x) => x.label === language.label) > -1) continue;

                const split = language.slug.split("-");
                languages.push({ ...language, slug: `int-${split[1]}`, regionLabel: "International" });
            }
        }

        setLanguages(languages.sort((a, b) => a.label.localeCompare(b.label)));
    };

    const onSubmit = (file: Download) => {
        downloads.push(file);
        setAddDownload(false);
    };

    const onSubmitCombined = (file: DynamicDownload) => {
        dynamicDownloads.push(file);
        setAddCombinedDownload(false);
    };

    const onClose = () => setAddDownload(false);

    const onDownload = (download: Download) => {
        const extension = `.${download.extension}`;
        let filename = download.name.trim();
        if (!filename.toLocaleLowerCase().endsWith(extension)) filename = `${filename}${extension}`;
        saveAs(download.location, filename);
    };

    const onEdit = (download: Download) => setEditDownload(download);
    const onEditClose = () => setEditDownload(null);
    const onEditSubmit = (download: Download) => {
        const index = downloads.findIndex((x) => x.id === download.id);
        downloads[index] = download;
        setDownloads([...downloads]);
        setEditDownload(null);
    };

    const onDelete = (id: string) => setDeleteDownload(id);

    const onDeleteDynamic = (id: string) => setDeleteDynamic(id);

    const onCancelDelete = () => {
        setDeleteDownload(null);
        setDeleteDynamic(null);
    };

    const onPerformDelete = () => {
        if (deleteDownload) {
            downloadService.delete(deleteDownload!).then((success) => {
                if (success) {
                    const list = downloads.filter((x) => x.id !== deleteDownload);
                    setDownloads(list);
                }

                setDeleteDownload(null);
            });
        } else if (deleteDynamic) {
            const download = dynamicDownloads.find((x) => x.id === deleteDynamic);
            if (!download) return;

            downloadService.deleteDynamic(download.bidsVersion, download.id).then((success) => {
                if (success) {
                    const list = dynamicDownloads.filter((x) => x.id !== download.id);
                    setDynamicDownloads(list);
                }

                setDeleteDynamic(null);
            });
        }
    };

    const handleChange = (_: React.SyntheticEvent, newValue: number) => setView(newValue);

    return (
        <>
            <Grid container spacing={4} p={4}>
                <Grid size={{ xs: 12, md: 12 }} sx={{ display: "flex" }}>
                    <Button
                        variant="contained"
                        startIcon={<PlusIcon className="w-5 h-5" />}
                        sx={{ ml: "auto" }}
                        onClick={() => setAddDownload(true)}
                    >
                        Add File
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<PlusIcon className="w-5 h-5" />}
                        sx={{ ml: "10px" }}
                        onClick={() => setAddCombinedDownload(true)}
                    >
                        Add Combined File
                    </Button>
                </Grid>
                <Grid size={{ xs: 12, md: 12 }}>
                    <Tabs value={view} onChange={handleChange} aria-label="basic tabs example">
                        <Tab label="Static Downloads" />
                        <Tab label="Dynamic Downloads" />
                    </Tabs>
                    <CustomTabPanel value={view} index={0}>
                        <TableContainer component={Paper}>
                            <Table sx={{ minWidth: 750 }} aria-label="simple table">
                                <TableHead sx={{ bgcolor: "alternate.main" }}>
                                    <TableRow>
                                        <TableCell>
                                            <Typography
                                                color={"text.secondary"}
                                                variant={"caption"}
                                                fontWeight={700}
                                                sx={{ textTransform: "uppercase" }}
                                            >
                                                Filename
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography
                                                color={"text.secondary"}
                                                variant={"caption"}
                                                fontWeight={700}
                                                sx={{ textTransform: "uppercase" }}
                                            >
                                                Extension
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography
                                                color={"text.secondary"}
                                                variant={"caption"}
                                                fontWeight={700}
                                                sx={{ textTransform: "uppercase" }}
                                            >
                                                Size
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography
                                                color={"text.secondary"}
                                                variant={"caption"}
                                                fontWeight={700}
                                                sx={{ textTransform: "uppercase" }}
                                            >
                                                Language
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography
                                                color={"text.secondary"}
                                                variant={"caption"}
                                                fontWeight={700}
                                                sx={{ textTransform: "uppercase" }}
                                            >
                                                Created
                                            </Typography>
                                        </TableCell>
                                        <TableCell />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {loading ? (
                                        <TableRow>
                                            <TableCell
                                                colSpan={5}
                                                sx={{
                                                    height: 400,
                                                    alignItems: "center",
                                                    flexDirection: "column",
                                                }}
                                            >
                                                <Box sx={{ display: "flex", placeContent: "center" }}>
                                                    <CircularProgress />
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ) : downloads.length > 0 ? (
                                        downloads.map((download, index) => {
                                            const size = getBytes(download.size);
                                            const language =
                                                languages.find((x) => x.slug === download.languageCode)?.label || "-";
                                            const creationDate = parseJSON(download.uploadDate).toLocaleString("nl-be");

                                            return (
                                                <TableRow
                                                    key={index}
                                                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                                                >
                                                    <TableCell component="th" scope="row" sx={{ verticalAlign: "top" }}>
                                                        <Typography color={"text.secondary"} variant={"subtitle2"}>
                                                            {download.name}
                                                        </Typography>
                                                        <a
                                                            href="#"
                                                            className="text-primary-500 text-xs"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                onDownload(download);
                                                            }}
                                                        >
                                                            (download)
                                                        </a>
                                                    </TableCell>
                                                    <TableCell>{download.extension}</TableCell>
                                                    <TableCell>{size}</TableCell>
                                                    <TableCell>{language}</TableCell>
                                                    <TableCell>{creationDate}</TableCell>
                                                    <TableCell>
                                                        <Button color={"primary"} onClick={() => onEdit(download)}>
                                                            Edit
                                                        </Button>
                                                        <Button
                                                            color={"error"}
                                                            sx={{ ml: 2, mr: 1 }}
                                                            onClick={() => onDelete(download.id)}
                                                        >
                                                            Delete
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    ) : (
                                        <TableRow>
                                            <TableCell
                                                colSpan={5}
                                                sx={{
                                                    height: 400,
                                                    alignItems: "center",
                                                    flexDirection: "column",
                                                }}
                                            >
                                                <Box sx={{ display: "flex", placeContent: "center" }}>
                                                    <Typography variant="body2">No files available.</Typography>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CustomTabPanel>
                    <CustomTabPanel value={view} index={1}>
                        <TableContainer component={Paper}>
                            <Table sx={{ minWidth: 750 }} aria-label="simple table">
                                <TableHead sx={{ bgcolor: "alternate.main" }}>
                                    <TableRow>
                                        <TableCell>
                                            <Typography
                                                color={"text.secondary"}
                                                variant={"caption"}
                                                fontWeight={700}
                                                sx={{ textTransform: "uppercase" }}
                                            >
                                                Filename
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography
                                                color={"text.secondary"}
                                                variant={"caption"}
                                                fontWeight={700}
                                                sx={{ textTransform: "uppercase" }}
                                            >
                                                Extension
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography
                                                color={"text.secondary"}
                                                variant={"caption"}
                                                fontWeight={700}
                                                sx={{ textTransform: "uppercase" }}
                                            >
                                                Size
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography
                                                color={"text.secondary"}
                                                variant={"caption"}
                                                fontWeight={700}
                                                sx={{ textTransform: "uppercase" }}
                                            >
                                                Language
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography
                                                color={"text.secondary"}
                                                variant={"caption"}
                                                fontWeight={700}
                                                sx={{ textTransform: "uppercase" }}
                                            >
                                                Created
                                            </Typography>
                                        </TableCell>
                                        <TableCell />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {loading ? (
                                        <TableRow>
                                            <TableCell
                                                colSpan={5}
                                                sx={{
                                                    height: 400,
                                                    alignItems: "center",
                                                    flexDirection: "column",
                                                }}
                                            >
                                                <Box sx={{ display: "flex", placeContent: "center" }}>
                                                    <CircularProgress />
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ) : dynamicDownloads.length > 0 ? (
                                        dynamicDownloads.map((download, index) => {
                                            const language =
                                                languages.find((x) => x.slug === download.language)?.label || "-";
                                            const creationDate = parseJSON(download.uploadDate).toLocaleString("nl-be");
                                            const versionId =
                                                versions.find((x) => x.name === download.bidsVersion)?.id || "";
                                            const versionSoftware = software[versionId];

                                            const softwareList =
                                                versionSoftware
                                                    ?.filter((x) => !x.isIfc)
                                                    .filter((x) => download.software?.includes(x.id))
                                                    .map((x) => x.name) ?? [];

                                            const ifcList =
                                                versionSoftware
                                                    ?.filter((x) => x.isIfc)
                                                    .filter((x) => download.ifcVersions?.includes(x.id))
                                                    .map((x) => x.name) ?? [];

                                            const useCaseList =
                                                useCases[versionId]
                                                    ?.filter((x) => download.useCases?.includes(x.id))
                                                    .map((x) => x.name?.trim()) ?? [];

                                            if (softwareList.length === 0) softwareList.push("-");
                                            if (ifcList.length === 0) ifcList.push("-");
                                            if (useCaseList.length === 0) useCaseList.push("-");

                                            return (
                                                <TableRow
                                                    key={index}
                                                    sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                                                >
                                                    <TableCell component="th" scope="row" sx={{ verticalAlign: "top" }}>
                                                        <Typography variant={"subtitle2"}>{download.name}</Typography>{" "}
                                                        <Typography color={"text.secondary"} variant="subtitle2">
                                                            <strong>Selected Discipline:</strong>{" "}
                                                            {disciplines[versionId]?.find(
                                                                (x) => x.id === download.disciplineId
                                                            )?.label || "-"}
                                                        </Typography>
                                                        <Typography color={"text.secondary"} variant="subtitle2">
                                                            <strong>Selected Software:</strong>{" "}
                                                            {softwareList?.join(", ")}
                                                        </Typography>
                                                        <Typography color={"text.secondary"} variant="subtitle2">
                                                            <strong>Detected IFC Versions:</strong>{" "}
                                                            {ifcList?.join(", ")}
                                                        </Typography>
                                                        <Typography color={"text.secondary"} variant="subtitle2">
                                                            <strong>Detected use-cases:</strong>{" "}
                                                            {useCaseList?.join(", ")}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>.zip</TableCell>
                                                    <TableCell>-</TableCell>
                                                    <TableCell>{language}</TableCell>
                                                    <TableCell>{creationDate}</TableCell>
                                                    <TableCell>
                                                        <Button
                                                            color={"error"}
                                                            sx={{ ml: 2, mr: 1 }}
                                                            onClick={() => onDeleteDynamic(download.id)}
                                                        >
                                                            Delete
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    ) : (
                                        <TableRow>
                                            <TableCell
                                                colSpan={5}
                                                sx={{
                                                    height: 400,
                                                    alignItems: "center",
                                                    flexDirection: "column",
                                                }}
                                            >
                                                <Box sx={{ display: "flex", placeContent: "center" }}>
                                                    <Typography variant="body2">No files available.</Typography>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CustomTabPanel>
                </Grid>
            </Grid>

            {addDownload && (
                <CreateDialog open={addDownload} languages={languages} onClose={onClose} onSubmit={onSubmit} />
            )}

            {addCombinedDownload && (
                <CreateCombinedDialog
                    open={addCombinedDownload}
                    languages={languages}
                    onClose={(_, reason) => {
                        if (reason === "backdropClick") return;
                        setAddCombinedDownload(false);
                    }}
                    onSubmit={onSubmitCombined}
                />
            )}

            {editDownload && <EditDialog download={editDownload} onClose={onEditClose} onSubmit={onEditSubmit} />}

            {(deleteDownload || deleteDynamic) && (
                <ConfirmDeleteDialog
                    open={Boolean(deleteDownload || deleteDynamic)}
                    title={"Delete file"}
                    message={"Are you sure you wish to delete this file? You can not undo this action!"}
                    onCancel={onCancelDelete}
                    onDelete={onPerformDelete}
                />
            )}
        </>
    );
};

export default DownloadManager;
